import { Constructor, Data, FieldValueTransformer } from './types';
import { arrayTypesMatch, getDef, isPresent, isSchema, isSerializer } from './utils';

export function deserialize<T, U extends Data>(schema: Constructor<T>, data: U[]): T[];
export function deserialize<T, U extends Data>(schema: Constructor<T>, data: U): T;
export function deserialize<T>(schema: Constructor<T>, data: any) {
	if (Array.isArray(data)) {
		return data.map((d) => deserialize(schema, d));
	}

	if (!isSchema(schema)) {
		throw new Error(`${schema.name} is not a schema`);
	}

	const def = getDef(schema);
	const instance = new schema();
	const assigns: Data = {};

	for (const attr of def.attrs()) {
		const from = attr.from;
		const field = attr.field;

		if (!from) {
			continue;
		}

		if (attr.isPk() && !isPresent(data[from.name])) {
			throw new Error(`Primary key for ${def.name} not found in data`);
		}

		if (!(from.name in data)) {
			if (!attr.isOptional()) {
				throw new Error(`Required field ${from.name} not found in data`);
			}

			continue;
		}

		const rawValue = data[from.name];

		if (!arrayTypesMatch(rawValue, field.transformer) && !isSerializer(field.transformer)) {
			throw new Error(`Data and transfomer must be the same type, field: ${field.transformer}, data: ${rawValue}`);
		}

		assigns[attr.name] = convertData(rawValue, field.transformer);
	}

	Object.assign(instance, assigns);

	return instance;
}

function convertData(data: any, fnArrayOrTsfmr: FieldValueTransformer): any {
	if (Array.isArray(data) && Array.isArray(fnArrayOrTsfmr)) {
		const [fnOrTsfrm] = fnArrayOrTsfmr;
		return data.map((d) => convertData(d, fnOrTsfrm));
	}

	if (!fnArrayOrTsfmr) {
		return data;
	}

	if (isSchema(fnArrayOrTsfmr)) {
		return data === null ? null : deserialize(fnArrayOrTsfmr, data);
	}

	if (isSerializer(fnArrayOrTsfmr)) {
		return new fnArrayOrTsfmr().deserialize(data);
	}

	// Don't pass null or data to String() because it will return 'null' or 'undefined'
	// Instead we just want an empty string
	if (data === null || data === undefined) {
		if (fnArrayOrTsfmr === String || fnArrayOrTsfmr === Array) {
			return fnArrayOrTsfmr();
		}
	}

	return fnArrayOrTsfmr(data);
}
